import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import type { Choice, NestedSelectOption, Question } from '@/types/form'
import { Plus, X } from 'lucide-react'

interface NestedSelectSettingsProps {
  question: Question
  onQuestionChange: (patch: Partial<Question>) => void
  onChange: (patch: Partial<Question['settings']>) => void
}

function optionFromChoice(choice: Choice): NestedSelectOption {
  return {
    id: choice.id,
    label: choice.label,
    value: choice.value,
    children: [],
  }
}

function createNestedOption(index: number, labelPrefix = '選項'): NestedSelectOption {
  return {
    id: crypto.randomUUID(),
    label: `${labelPrefix} ${index + 1}`,
    value: `${labelPrefix.toLowerCase()}-${index + 1}`,
    children: [],
  }
}

function updateOption(
  options: NestedSelectOption[],
  optionId: string,
  patch: Partial<NestedSelectOption>,
): NestedSelectOption[] {
  return options.map((option) => {
    if (option.id === optionId) return { ...option, ...patch }
    return {
      ...option,
      children: option.children ? updateOption(option.children, optionId, patch) : option.children,
    }
  })
}

function removeOption(options: NestedSelectOption[], optionId: string): NestedSelectOption[] {
  return options
    .filter((option) => option.id !== optionId)
    .map((option) => ({
      ...option,
      children: option.children ? removeOption(option.children, optionId) : option.children,
    }))
}

function addChildOption(
  options: NestedSelectOption[],
  parentId: string,
  labelPrefix: string,
): NestedSelectOption[] {
  return options.map((option) => {
    if (option.id === parentId) {
      const children = option.children ?? []
      return {
        ...option,
        children: [...children, createNestedOption(children.length, labelPrefix)],
      }
    }
    return {
      ...option,
      children: option.children
        ? addChildOption(option.children, parentId, labelPrefix)
        : option.children,
    }
  })
}

function rootChoicesFromOptions(question: Question, options: NestedSelectOption[]): Choice[] {
  return options.map((option, orderIndex) => ({
    id: option.id,
    questionId: question.id,
    label: option.label,
    value: option.value,
    orderIndex,
  }))
}

export function NestedSelectSettings({
  question,
  onQuestionChange,
  onChange,
}: NestedSelectSettingsProps) {
  const levels = question.settings.nestedLevels ?? ['第一層', '第二層']
  const options = question.settings.nestedOptions?.length
    ? question.settings.nestedOptions
    : question.choices.length > 0
      ? question.choices.map(optionFromChoice)
      : [createNestedOption(0, levels[0] ?? '選項')]

  function commitOptions(nextOptions: NestedSelectOption[]) {
    onChange({ nestedOptions: nextOptions })
    onQuestionChange({ choices: rootChoicesFromOptions(question, nextOptions) })
  }

  return (
    <div className="flex flex-col gap-4 rounded-lg border border-slate-200 p-3">
      <div>
        <h4 className="text-sm font-semibold text-slate-950">多層選單設定</h4>
        <p className="mt-1 text-xs leading-5 text-slate-500">
          這裡的選項樹會直接用於 Builder 預覽與填答頁，避免第一層選項與預覽子選項不同步。
        </p>
      </div>

      <div className="flex flex-col gap-2">
        <Label>階層名稱</Label>
        <Input
          value={levels.join(' / ')}
          placeholder="區域 / 縣市 / 據點"
          onChange={(event) =>
            onChange({
              nestedLevels: event.target.value
                .split('/')
                .map((item) => item.trim())
                .filter(Boolean),
            })
          }
        />
        <p className="text-xs text-slate-500">使用斜線分隔階層，例如：區域 / 縣市 / 據點。</p>
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between gap-3">
          <Label>選項樹</Label>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() =>
              commitOptions([...options, createNestedOption(options.length, levels[0])])
            }
          >
            <Plus className="size-4" />
            新增第一層
          </Button>
        </div>
        <div className="space-y-3">
          {options.map((option) => (
            <NestedOptionEditor
              key={option.id}
              option={option}
              level={0}
              levels={levels}
              canRemove={options.length > 1}
              onPatch={(patch) => commitOptions(updateOption(options, option.id, patch))}
              onRemove={() => commitOptions(removeOption(options, option.id))}
              onAddChild={(parentId, labelPrefix) =>
                commitOptions(addChildOption(options, parentId, labelPrefix))
              }
            />
          ))}
        </div>
      </div>

      <NestedOptionsPreview levels={levels} options={options} />
    </div>
  )
}

function NestedOptionEditor({
  option,
  level,
  levels,
  canRemove,
  onPatch,
  onRemove,
  onAddChild,
}: {
  option: NestedSelectOption
  level: number
  levels: string[]
  canRemove: boolean
  onPatch: (patch: Partial<NestedSelectOption>) => void
  onRemove: () => void
  onAddChild: (parentId: string, labelPrefix: string) => void
}) {
  const nextLevel = level + 1
  const canAddChild = nextLevel < levels.length
  const children = option.children ?? []

  return (
    <div
      className="rounded-md border border-slate-200 bg-white p-2"
      style={{ marginLeft: level ? 12 : 0 }}
    >
      <div className="grid grid-cols-[1fr_1fr_auto] gap-2">
        <Input
          value={option.label}
          aria-label={`${levels[level] ?? `第 ${level + 1} 層`}名稱`}
          onChange={(event) => onPatch({ label: event.target.value })}
        />
        <Input
          value={option.value}
          aria-label={`${levels[level] ?? `第 ${level + 1} 層`}值`}
          onChange={(event) => onPatch({ value: event.target.value })}
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          disabled={!canRemove}
          onClick={onRemove}
          aria-label="移除選項"
        >
          <X className="size-4" />
        </Button>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-2">
        <span className="rounded-full bg-slate-100 px-2 py-1 text-xs text-slate-500">
          {levels[level] ?? `第 ${level + 1} 層`}
        </span>
        {canAddChild && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => onAddChild(option.id, levels[nextLevel] ?? '子選項')}
          >
            <Plus className="size-4" />
            新增{levels[nextLevel] ?? '子選項'}
          </Button>
        )}
      </div>

      {children.length > 0 && (
        <div className="mt-3 space-y-2 border-l border-slate-200 pl-3">
          {children.map((child) => (
            <NestedOptionEditor
              key={child.id}
              option={child}
              level={nextLevel}
              levels={levels}
              canRemove
              onPatch={(patch) => onPatch({ children: updateOption(children, child.id, patch) })}
              onRemove={() => onPatch({ children: removeOption(children, child.id) })}
              onAddChild={(parentId, labelPrefix) =>
                onPatch({
                  children: addChildOption(children, parentId, labelPrefix),
                })
              }
            />
          ))}
        </div>
      )}
    </div>
  )
}

function NestedOptionsPreview({
  levels,
  options,
}: {
  levels: string[]
  options: NestedSelectOption[]
}) {
  const path = findFirstPath(options)
  return (
    <div className="rounded-md border border-indigo-100 bg-indigo-50 p-3 text-xs leading-5 text-indigo-800">
      <div className="font-medium">填答預覽路徑</div>
      <div className="mt-1">
        {path.length > 0 ? path.map((item) => item.label).join(' / ') : '尚未建立可選路徑'}
      </div>
      <div className="mt-2 text-indigo-700">
        {levels.length > 0
          ? `Player 會依序顯示：${levels.join(' / ')}。`
          : '請先設定階層名稱。'}
      </div>
    </div>
  )
}

function findFirstPath(options: NestedSelectOption[]): NestedSelectOption[] {
  const first = options[0]
  if (!first) return []
  return [first, ...findFirstPath(first.children ?? [])]
}
